
import React, { useState, useEffect } from 'react';
import { X, Share, MoreVertical, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AddToHomeScreenProps {
  embedded?: boolean;
}

const AddToHomeScreen: React.FC<AddToHomeScreenProps> = ({ embedded = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>('other');
  const { t } = useTranslation();

  useEffect(() => {
    // If embedded, always show the component
    if (embedded) {
      detectPlatform();
      setIsVisible(true);
      return;
    }

    // Check if user has completed onboarding or already prompted
    const hasPrompted = localStorage.getItem('homeScreenPromptShown');
    const userData = localStorage.getItem('userData');
    const isOnboardingComplete = userData ? JSON.parse(userData).onboardingCompleted : false;
    
    // Don't show prompt if already prompted or just completed onboarding
    if (hasPrompted || isOnboardingComplete) return;

    // Add a short delay to ensure the component renders after page load
    const timer = setTimeout(() => {
      detectPlatform();
      setIsVisible(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, [embedded]);

  const detectPlatform = () => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else {
      // For desktop or other devices, show Android instructions by default
      setPlatform('android');
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Only set the flag when dismissed
    localStorage.setItem('homeScreenPromptShown', 'true');
  };

  // Don't render anything if not visible and not embedded
  if (!isVisible && !embedded) return null;

  const instructions = (
    <div className={embedded ? "" : "fixed bottom-20 left-4 right-4 bg-white rounded-lg shadow-lg p-4 z-50 animate-fade-in border border-teal-200"}>
      <div className="flex items-start">
        <div className="flex-1">
          {!embedded && <h3 className="font-semibold text-gray-800 mb-2">{t('install.title')}</h3>}
          
          {platform === 'ios' ? (
            <div className="text-sm text-gray-600">
              <div className="mb-2 flex items-center">
                <span>{t('install.instructions.ios.step1')}</span>
                <Share size={18} className="mx-1 text-blue-500" />
                <span>{t('install.instructions.ios.step2')}</span>
              </div>
              <div className="flex items-center">
                <span>{t('install.instructions.ios.step2')}</span>
                <span className="mx-1 bg-gray-100 rounded px-1 py-0.5 text-xs font-medium">{t('install.instructions.ios.addToHome')}</span>
              </div>
              <div className="mt-2 border border-gray-200 rounded p-2 bg-gray-50">
                <div className="flex items-center justify-center mb-1">
                  <Share size={20} className="text-blue-500" />
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-20 h-6 bg-white rounded-md border border-gray-300 mb-1 flex items-center justify-center">
                    <span className="text-xs">{t('install.instructions.ios.addToHome')}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              <div className="mb-2 flex items-center">
                <span>{t('install.instructions.android.step1')}</span>
                <MoreVertical size={18} className="mx-1 text-gray-700" />
                <span>{t('install.instructions.android.step2')}</span>
                <Menu size={18} className="mx-1 text-gray-700" />
                <span>{t('install.instructions.android.step2')}</span>
              </div>
              <div className="flex items-center">
                <span>{t('install.instructions.android.step2')}</span>
                <span className="mx-1 bg-gray-100 rounded px-1 py-0.5 text-xs font-medium">{t('install.instructions.android.addToHome')}</span>
              </div>
              <div className="mt-2 border border-gray-200 rounded p-2 bg-gray-50">
                <div className="flex items-center justify-end mb-1">
                  <MoreVertical size={20} className="text-gray-700" />
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-48 h-6 bg-white rounded-md border border-gray-300 mb-1 flex items-center justify-center">
                    <span className="text-xs">{t('install.instructions.android.addToHome')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {!embedded && (
          <button 
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-700 rounded-full p-1"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </div>
  );

  return instructions;
};

export default AddToHomeScreen;
